/* Add to Google Calendar — a pre-filled template link, not an API.
 *
 * Clicking opens Google Calendar with the event filled in; the person
 * presses Save. There is no Cloud project, no OAuth screen, no tokens to
 * expire, and nothing that can break silently at 3am. The trade is that the
 * link is one-way and one-time: see `needsReAdd` below.
 *
 * Jamaica is UTC−5 all year with no DST, which is why this can be a fixed
 * zone rather than anything seasonal.
 */

export const CALENDAR_TZ = 'America/Jamaica';

const BASE = 'https://calendar.google.com/calendar/render';

const pad = (n) => String(n).padStart(2, '0');

// Dates are handled as strings throughout, never via Date parsing —
// `new Date('2026-09-15')` is UTC midnight, which in Jamaica is the previous
// day, and would put every all-day event on the wrong date.
function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function compact(dateStr) {
  return dateStr.replace(/-/g, '');
}

function addMinutes(timeStr, minutes) {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + minutes;
  // Clamp rather than roll into the next day: a task due at 23:50 gets a
  // 23:50–23:59 slot instead of an event Google reads as ending before it
  // starts.
  const capped = Math.min(total, 24 * 60 - 1);
  return `${pad(Math.floor(capped / 60))}:${pad(capped % 60)}`;
}

export const EVENT_MINUTES = 30;

/* The `dates` parameter.
 *
 * Two things bite here, and both are silent:
 *   - On all-day events Google treats the end as EXCLUSIVE, so a one-day
 *     event must end on the following day. Passing the same date twice
 *     makes the event vanish or land a day early.
 *   - Never append `Z` while also sending ctz. `Z` means UTC and shifts a
 *     Jamaican event by five hours. Send local wall-clock time and let ctz
 *     do the work.
 */
export function buildDates(task) {
  if (!task?.due_date) return null;

  if (task.due_time) {
    const time = String(task.due_time).slice(0, 5);
    const start = `${compact(task.due_date)}T${time.replace(':', '')}00`;
    const endTime = addMinutes(time, EVENT_MINUTES);
    const end = `${compact(task.due_date)}T${endTime.replace(':', '')}00`;
    return `${start}/${end}`;
  }

  return `${compact(task.due_date)}/${compact(addDays(task.due_date, 1))}`;
}

export function googleCalendarUrl(task, appUrl = '') {
  const dates = buildDates(task);
  if (!dates) return null;

  const details = [
    task.notes || null,
    task.link_label ? `Related: ${task.link_label}` : null,
    appUrl ? `Open in dashboard: ${appUrl}/todo/${task.id}` : null,
  ]
    .filter(Boolean)
    .join('\n\n');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: task.title || 'Task',
    ctz: CALENDAR_TZ,
    dates,
  });
  if (details) params.set('details', details);

  return `${BASE}?${params.toString()}`;
}

/* Whether the saved event is now out of date.
 *
 * The link cannot move an event that already exists in Google — clicking
 * Add again creates a second one. So when the due date changes after it was
 * added, the button has to say so rather than quietly pretend the calendar
 * is right.
 */
export function needsReAdd(task, previousDueDate, previousDueTime) {
  if (!task?.added_to_calendar_at) return false;
  return task.due_date !== previousDueDate || (task.due_time || null) !== (previousDueTime || null);
}
